import type { OperationStepStatus, PrinterOperation } from "./printers-api";

/**
 * Czasy kroków operacji widziane przez panel. API nie podaje czasu kroku, a
 * agent zgłasza kroki jednej komendy razem (jeden ack), więc panel zapisuje
 * chwilę, w której ZOBACZYŁ zmianę stanu kroku przy odpytywaniu co 2 s.
 * Kroki zgłoszone w tym samym acku dostają ten sam koniec — to prawda o tym,
 * co wiemy, nie podział czasu wymyślony w panelu. Operacja zakończona, zanim
 * panel ją zobaczył (historia), nie ma czasów kroków — tylko czas całości.
 */
export type StepTime = { start: number | null; end: number | null };
export type OperationTimeline = {
	/** Chwila pierwszej obserwacji operacji przez panel. */
	firstSeen: number;
	steps: Record<string, StepTime>;
};

const STORAGE_KEY = "farmaster_drukarki_step_times";
/** Ile ostatnich operacji pamiętamy (przeładowanie strony w trakcie planu). */
const KEEP = 40;

const DONE: ReadonlySet<OperationStepStatus> = new Set(["ok", "failed", "skipped"]);

let cache: Map<number, OperationTimeline> | null = null;

function load(): Map<number, OperationTimeline> {
	if (cache) return cache;
	cache = new Map();
	try {
		const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
		if (raw) {
			for (const [id, timeline] of JSON.parse(raw) as [number, OperationTimeline][]) {
				cache.set(id, timeline);
			}
		}
	} catch {
		// Uszkodzony wpis — zaczynamy od zera, czasy kroków to tylko podpowiedź.
	}
	return cache;
}

function persist(map: Map<number, OperationTimeline>) {
	try {
		const entries = [...map.entries()].slice(-KEEP);
		globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(entries));
	} catch {
		// Brak miejsca albo tryb prywatny — czasy zostają w pamięci karty.
	}
}

/**
 * Zapisz, co panel widzi teraz. Pierwsza obserwacja ZAKOŃCZONEJ operacji nie
 * tworzy czasów (nie wiemy, kiedy kroki się kończyły).
 */
export function observeOperation(operation: PrinterOperation, now: number = Date.now()): void {
	const map = load();
	let timeline = map.get(operation.operationId);
	if (!timeline) {
		if (operation.status !== "running") return;
		timeline = { firstSeen: now, steps: {} };
		map.set(operation.operationId, timeline);
	}
	let changed = false;
	for (const step of operation.steps) {
		const current = timeline.steps[step.step] ?? { start: null, end: null };
		const next = { ...current };
		if (step.status === "running" && next.start == null) next.start = now;
		if (DONE.has(step.status) && next.end == null) next.end = now;
		if (next.start !== current.start || next.end !== current.end || !timeline.steps[step.step]) {
			timeline.steps[step.step] = next;
			changed = true;
		}
	}
	if (changed) persist(map);
}

export function operationTimeline(operationId: number): OperationTimeline | null {
	return load().get(operationId) ?? null;
}

/** Tylko do testów: wyczyść pamięć modułu. */
export function resetStepTimesForTests(): void {
	cache = null;
	try {
		globalThis.localStorage?.removeItem(STORAGE_KEY);
	} catch {
		// bez znaczenia w testach
	}
}

export type WaterfallBar = {
	step: string;
	status: OperationStepStatus;
	/** Ms od startu operacji; `null` = brak czasu (historia, krok nieobserwowany). */
	from: number | null;
	to: number | null;
	/** Czas kroku do pokazania; `null` = nieznany albo zgłoszony razem z poprzednim. */
	duration: number | null;
	/** Krok zgłoszony w tym samym acku co poprzedni — bez osobnego czasu. */
	sameAck: boolean;
};

/**
 * Paski wodospadu z czasów obserwacji. Oś liczy się od pierwszej obserwacji
 * operacji zegarem przeglądarki (bez mieszania z czasem serwera — rozjazd
 * zegarów przesunąłby paski). Krok bez własnego startu zaczyna się, gdy
 * skończył się poprzedni (agent wykonuje kroki komendy po kolei).
 */
export function waterfallBars(
	operation: PrinterOperation,
	timeline: OperationTimeline | null,
	now: number = Date.now(),
): { bars: WaterfallBar[]; span: number; timed: boolean } {
	if (!timeline) {
		return {
			bars: operation.steps.map((step) => ({
				step: step.step,
				status: step.status,
				from: null,
				to: null,
				duration: null,
				sameAck: false,
			})),
			span: 1,
			timed: false,
		};
	}
	const origin = timeline.firstSeen;
	let prevEnd = 0;
	let prevAckEnd: number | null = null;
	let lastEnd = 0;
	const bars = operation.steps.map((step): WaterfallBar => {
		const t = timeline.steps[step.step] ?? { start: null, end: null };
		const start = t.start != null ? t.start - origin : prevEnd;
		const end = t.end != null ? t.end - origin : step.status === "running" ? now - origin : null;
		const sameAck = t.end != null && prevAckEnd != null && t.end - origin === prevAckEnd;
		if (t.end != null) prevAckEnd = t.end - origin;
		if (end != null) {
			prevEnd = end;
			lastEnd = Math.max(lastEnd, end);
		}
		const duration = end != null && !sameAck && end > start ? end - start : null;
		return { step: step.step, status: step.status, from: start, to: end, duration, sameAck };
	});
	const axisEnd = operation.status === "running" ? now - origin : lastEnd;
	return { bars, span: Math.max(1, axisEnd), timed: true };
}

/** „≈4 s”, „≈1 min 5 s” — siatka odpytywania to 2 s, więc bez ułamków. */
export function formatStepDuration(ms: number): string {
	const seconds = Math.max(1, Math.round(ms / 1000));
	if (seconds < 60) return `≈${seconds} s`;
	const minutes = Math.floor(seconds / 60);
	const rest = seconds % 60;
	return rest ? `≈${minutes} min ${rest} s` : `≈${minutes} min`;
}

/** Czas całej operacji z `createdAt`/`finishedAt` (dokładny — liczy backend). */
export function operationDuration(operation: PrinterOperation, now: number = Date.now()): number {
	const start = new Date(operation.createdAt).getTime();
	const end = operation.finishedAt ? new Date(operation.finishedAt).getTime() : now;
	return Math.max(0, end - start);
}

export function formatTotalDuration(ms: number): string {
	const seconds = Math.round(ms / 1000);
	if (seconds < 60) return `${seconds} s`;
	const minutes = Math.floor(seconds / 60);
	const rest = seconds % 60;
	return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
}
